export const AMPI_ORGANIZATION_IDS = Object.freeze([
  "62b622c66a7441ae20159d7d",
  "6297b0fec7bf8b6f826313be",
]);

// Keep this list aligned with the legacy permission subjects. Unknown subjects
// returned by the API are also elevated below so new legacy modules do not
// silently become unavailable to AMPI delegated admins.
export const LEGACY_PERMISSION_SUBJECTS = Object.freeze([
  "Event",
  "Membership",
  "Permission",
  "Payouts",
  "Purchase",
  "Transaction",
  "Panel",
  "Help",
  "Dispersions",
  "Account",
  "Chapters",
  "Members",
  "reportsDownload",
  "AuditLog",
  "Invoice",
]);

const referenceId = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return referenceId(value._id || value.id || value.organization);
  }

  return String(value);
};

export function getOrganizationId(user = {}) {
  const headOfficeId = referenceId(user.headOffice);
  if (headOfficeId) return headOfficeId;

  const organizationId = referenceId(user.organization);
  if (organizationId) return organizationId;

  const contextId = referenceId(user.organizationContext);
  if (contextId) return contextId;

  return referenceId(user._id);
}

export function isAmpiOrganizationUser(user = {}) {
  if (!user || user.role !== "ORGANIZATION_USER") return false;

  const organizationIds = [user.organization, user.headOffice]
    .map(referenceId)
    .filter(Boolean);

  if (organizationIds.length === 0) {
    organizationIds.push(referenceId(user.organizationContext));
  }

  return organizationIds.some((organizationId) =>
    AMPI_ORGANIZATION_IDS.includes(organizationId)
  );
}

export function getOrganizationContext(user = {}) {
  if (!user) return null;
  if (user.organizationContext) return user.organizationContext;
  if (user.headOffice && typeof user.headOffice === "object") {
    return user.headOffice;
  }
  return user;
}

export function hasOrganizationChapters(user = {}) {
  if (isAmpiOrganizationUser(user)) return true;
  return getOrganizationContext(user)?.hierarchyType === "TWO_LEVELS";
}

export function allowsOrganizationSubmembers(user = {}) {
  const owner = getOrganizationContext(user);
  return owner ? owner.allowSubmembers !== false : true;
}

const permissionEntries = (permissions) => {
  if (Array.isArray(permissions)) return permissions;
  if (!permissions || typeof permissions !== "object") return [];

  return Object.entries(permissions).map(([subject, permission]) => ({
    ...(permission && typeof permission === "object" ? permission : {}),
    subject: permission?.subject || subject,
  }));
};

// Keep the shape expected by the legacy dashboard stable for every user. A
// valid permission map is returned unchanged; missing or malformed values are
// converted to an empty map without granting any access.
export function ensurePermissionMap(user) {
  if (!user || typeof user !== "object" || Array.isArray(user)) return user;
  if (!Array.isArray(user.permissions)) {
    return user.permissions && typeof user.permissions === "object"
      ? user
      : { ...user, permissions: {} };
  }

  const permissions = {};
  user.permissions.forEach((permission) => {
    if (permission && typeof permission === "object" && permission.subject) {
      permissions[permission.subject] = permission;
    }
  });

  return { ...user, permissions };
}

export function normalizeAmpiOrganizationUser(user) {
  if (!isAmpiOrganizationUser(user)) return user;

  const entries = permissionEntries(user.permissions);
  const permissionsBySubject = new Map();

  entries.forEach((permission) => {
    if (permission?.subject) {
      permissionsBySubject.set(permission.subject, permission);
    }
  });

  const subjects = new Set([
    ...LEGACY_PERMISSION_SUBJECTS,
    ...permissionsBySubject.keys(),
  ]);

  const permissions = {};
  subjects.forEach((subject) => {
    permissions[subject] = {
      ...(permissionsBySubject.get(subject) || {}),
      subject,
      create: true,
      read: true,
      update: true,
      delete: true,
    };
  });

  return {
    ...user,
    permissions,
  };
}
